import { computed, effect, Signal } from '@preact/signals'

import { api } from './api.ts'
import { url } from '@01edu/signal-router'
import { entries } from '../../api/entries.ts'

type EntryRecord = typeof entries
type EntryKey = keyof EntryRecord
type EntryType = EntryRecord[EntryKey]

export const user = api['GET/api/user/me'].signal()
export const sourceDisconnectedAt = new Signal(Date.now())

type Handler = (at: number, type: EntryType) => void
const listeners = new Set<{ type: EntryType; handler: Handler }>()

export const onEvent = (
  entryTypes: EntryKey | EntryKey[],
  handler: Handler,
) => {
  const disposable: { type: EntryType; handler: Handler }[] = []
  for (const key of Array.isArray(entryTypes) ? entryTypes : [entryTypes]) {
    const listener = { type: entries[key], handler }
    disposable.push(listener)
    listeners.add(listener)
  }

  return () => {
    for (const listener of disposable) {
      listeners.delete(listener)
    }
  }
}

let source: EventSource
const setDisconnected = () =>
  sourceDisconnectedAt.peek() || (sourceDisconnectedAt.value = Date.now())

effect(() => {
  const email = user.data?.userEmail
  if (!email) return
  const sourceUrl = `/api/events?${new URLSearchParams({
    project: String(Number(url.params.project) || 0),
    license: String(Number(url.params.license) || 0),
  })}`
  if (source) {
    if (source.url.endsWith(sourceUrl)) return
    source.close()
  }
  setDisconnected()
  source = new EventSource(sourceUrl)
  source.addEventListener('message', (event) => {
    const [at, type] = JSON.parse(event.data) as [number, EntryType]
    for (const listener of listeners) {
      if (listener.type !== type) continue
      try {
        listener.handler(at, type)
      } catch (err) {
        console.error(err)
      }
    }
  })

  let timeout = setTimeout(setDisconnected, 20_000)
  source.addEventListener('ack', () => {
    sourceDisconnectedAt.peek() && (sourceDisconnectedAt.value = 0)
    clearTimeout(timeout)
    timeout = setTimeout(setDisconnected, 20_000)
  })

  source.addEventListener('open', () => {
    sourceDisconnectedAt.value = 0
  })

  source.addEventListener('error', setDisconnected)
})

export const isAdmin = computed(() =>
  user.data?.userEmail?.endsWith('@01edu.ai') ?? false
)

export const projectOwners = api['GET/api/project/owners'].signal()

effect(() => {
  const projectId = url.params.project
  if (projectId && user.data) {
    projectOwners.fetch({ project: projectId })
  }
})

user.fetch()
